import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { ThemeToggle } from "@/components/theme-toggle";

import {
  analyze,
  getSettings,
  logout,
  putSettings,
  type AnalysisDetail,
  type AnalyzeResponse,
  type Me,
  type Row,
  type Settings,
} from "@/lib/api";
import { useTracker } from "@/lib/useTracker";
import { RankTable, type Mode } from "@/components/RankTable";
import { TickerDetail } from "@/components/TickerDetail";
import { SettingsSheet } from "@/components/SettingsSheet";
import { ScannerPanel } from "@/components/ScannerPanel";
import { SystemStatusBar } from "@/components/SystemStatusBar";
import { UserMenu } from "@/components/UserMenu";

type RunState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; response: AnalyzeResponse };

type DashboardProps = {
  // Authenticated MT user, or null in legacy (no-auth) mode. The header
  // conditionally renders the UserMenu vs. plain Refresh/Settings buttons
  // based on me?.authenticated.
  me: Me | null;
  version: string;
  navigate: (to: string) => void;
};

export function Dashboard({ me, version, navigate }: DashboardProps) {
  const [tickersInput, setTickersInput] = useState("");
  const [capitalInput, setCapitalInput] = useState("");
  const [lookback, setLookback] = useState("");
  const [benchmark, setBenchmark] = useState("");
  const [mode, setMode] = useState<Mode>("all");
  const [compact, setCompact] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [state, setState] = useState<RunState>({ kind: "idle" });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trackPollSeconds, setTrackPollSeconds] = useState(300);
  const [currentSettings, setCurrentSettings] = useState<Settings | null>(null);
  // Ad-hoc analyses for tickers that aren't in the main watchlist (e.g.
  // a scanner hit the user clicks). Kept separate from `state` so a
  // one-off detail-view run doesn't rewrite the main table. Both maps
  // are keyed by ticker and merged into the TickerDetail lookup below.
  const [adHocAnalyses, setAdHocAnalyses] = useState<Record<string, AnalysisDetail>>({});
  const [adHocRows, setAdHocRows] = useState<Record<string, Row>>({});
  // Tickers currently being analyzed on demand (scanner row click). The
  // TickerDetail sheet already shows "Loading…" when row+detail aren't
  // both present, so this only guards against duplicate POSTs when a
  // user double-clicks or taps the same row twice.
  const [adHocLoading, setAdHocLoading] = useState<Record<string, boolean>>({});

  const onLogout = async () => {
    // Hand off to the auth service's redirect-style logout so its
    // refresh cookie also gets cleared. Without this hop the next
    // navigation silently re-mints a launch token and the user
    // appears logged in again — broken-button UX. The server returns
    // post_logout_url even in legacy mode (it's "/" then), so this
    // works in both code paths.
    let dest = "/";
    try {
      const resp = await logout();
      if (resp.post_logout_url) dest = resp.post_logout_url;
    } catch {
      // Fall through to a local-only home redirect; even on logout
      // failure the local cookie is already cleared.
    }
    window.location.href = dest;
  };

  // Only subscribe to the SSE stream once we have a baseline on the
  // server — before the first analyze, the tracker has nothing to
  // emit. Avoids a dangling connection during the initial page load.
  const trackerEnabled = state.kind === "ready";
  const { snapshots: trackerSnapshots } = useTracker(trackerEnabled);

  const loadSettingsIntoForm = () => {
    void getSettings()
      .then((s) => {
        setCurrentSettings(s);
        setTickersInput(s.DefaultWatchlist.join(" "));
        setCapitalInput(String(s.DefaultCapital));
        setLookback(s.DefaultLookback);
        if (s.TrackPollSeconds > 0) setTrackPollSeconds(s.TrackPollSeconds);
      })
      .catch(() => {
        setTickersInput("TSLA NVDA AAPL MSFT");
        setCapitalInput("10000");
        setLookback("180d");
      })
      .finally(() => setSettingsLoaded(true));
  };

  const patchSettings = async (patch: Partial<Record<string, unknown>>) => {
    const updated = await putSettings(patch);
    setCurrentSettings(updated);
  };

  const promoteTicker = async (ticker: string) => {
    if (!currentSettings) return;
    const t = ticker.toUpperCase();
    if (currentSettings.DefaultWatchlist.includes(t)) return;
    const next = [...currentSettings.DefaultWatchlist, t];
    await patchSettings({ default_watchlist: next });
    setTickersInput(next.join(" "));
  };

  // handleScannerSelect opens the TickerDetail sheet for a scanner row.
  // If the ticker isn't already in the main-table analyses or in the
  // ad-hoc cache, it fires a one-ticker /api/analyze so the sheet gets
  // full chart data + company info. Runs asynchronously — the sheet
  // opens immediately and the TickerDetail "Loading…" fallback covers
  // the window until the response lands.
  const handleScannerSelect = (ticker: string) => {
    const t = ticker.toUpperCase();
    setSelected(t);
    const alreadyAnalyzed =
      (state.kind === "ready" && state.response.analyses[t]) ||
      adHocAnalyses[t];
    if (alreadyAnalyzed || adHocLoading[t]) return;
    setAdHocLoading((m) => ({ ...m, [t]: true }));
    void analyze({
      tickers: [t],
      lookback: lookback || undefined,
      capital: Number(capitalInput) || undefined,
      benchmark: benchmark.trim() || undefined,
      // One-off scanner-row detail lookup — don't clobber the main
      // watchlist's tracker baselines with just this ticker.
      skip_tracker: true,
    })
      .then((resp) => {
        const detail = resp.analyses[t];
        const row = resp.rows_by_mode.all.find((r) => r.ticker === t);
        if (detail) {
          setAdHocAnalyses((m) => ({ ...m, [t]: detail }));
        }
        if (row) {
          setAdHocRows((m) => ({ ...m, [t]: row }));
        }
      })
      .catch((err: unknown) => {
        console.error("scanner analyze failed:", err);
      })
      .finally(() => {
        setAdHocLoading((m) => {
          const next = { ...m };
          delete next[t];
          return next;
        });
      });
  };

  useEffect(() => {
    loadSettingsIntoForm();
  }, []);

  const runAnalyzeWith = async (opts: { noCache: boolean }) => {
    // Refreshes keep the current table visible so the rows don't flash
    // to a skeleton; a separate "refreshing" flag drives the button's
    // spinner. Initial runs use the full loading skeleton.
    if (opts.noCache && state.kind === "ready") {
      setRefreshing(true);
    } else {
      setState({ kind: "loading" });
    }
    const tickers = tickersInput
      .split(/[\s,]+/)
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);
    const capital = Number(capitalInput) || undefined;
    try {
      const response = await analyze({
        tickers,
        capital,
        lookback,
        benchmark: benchmark.trim() || undefined,
        no_cache: opts.noCache || undefined,
      });
      setState({ kind: "ready", response });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRefreshing(false);
    }
  };

  const runAnalyze = () => runAnalyzeWith({ noCache: false });
  const runRefresh = () => runAnalyzeWith({ noCache: true });

  const rows = state.kind === "ready" ? state.response.rows_by_mode[mode] : [];
  const analyses = state.kind === "ready" ? state.response.analyses : {};
  // Fall back to the 'all' slice so a ticker selected in one mode stays
  // resolvable after the user flips to a mode that filters it out.
  const allRows = state.kind === "ready" ? state.response.rows_by_mode.all : [];
  const selectedRow =
    rows.find((r) => r.ticker === selected) ??
    allRows.find((r) => r.ticker === selected) ??
    (selected ? adHocRows[selected] : undefined);
  const selectedDetail = selected
    ? analyses[selected] ?? adHocAnalyses[selected]
    : undefined;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-[1600px] px-6 py-4 space-y-3">
          <div className="flex items-baseline justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">taco</h1>
              <p className="text-sm text-muted-foreground">
                Swing-trade analyzer: support/resistance, risk, and ranking
              </p>
            </div>
            <div className="flex items-center gap-2">
              {me?.authenticated ? (
                <UserMenu
                  me={me}
                  refreshing={refreshing}
                  settingsLoaded={settingsLoaded}
                  canRefresh={state.kind === "ready"}
                  onRefresh={runRefresh}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onNavigateAdmin={() => navigate("/admin")}
                  onLogout={onLogout}
                />
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runRefresh}
                    disabled={
                      state.kind !== "ready" || refreshing || !settingsLoaded
                    }
                    title="Re-fetch all tickers, bypassing the 4-hour cache"
                  >
                    {refreshing ? "Refreshing…" : "Refresh"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSettingsOpen(true)}
                  >
                    Settings
                  </Button>
                </>
              )}
              <ThemeToggle />
            </div>
          </div>
          <SystemStatusBar />
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Run</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <Label htmlFor="tickers" className="text-xs">
                  Tickers (space or comma separated)
                </Label>
                <Input
                  id="tickers"
                  value={tickersInput}
                  onChange={(e) => setTickersInput(e.target.value)}
                  placeholder="TSLA NVDA AAPL"
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="capital" className="text-xs">
                  Capital ($)
                </Label>
                <Input
                  id="capital"
                  value={capitalInput}
                  onChange={(e) => setCapitalInput(e.target.value)}
                  type="number"
                  placeholder="10000"
                />
              </div>
              <div>
                <Label htmlFor="lookback" className="text-xs">
                  Lookback
                </Label>
                <Input
                  id="lookback"
                  value={lookback}
                  onChange={(e) => setLookback(e.target.value)}
                  placeholder="180d"
                />
              </div>
              <div>
                <Label htmlFor="benchmark" className="text-xs">
                  Benchmark override (optional)
                </Label>
                <Input
                  id="benchmark"
                  value={benchmark}
                  onChange={(e) => setBenchmark(e.target.value)}
                  placeholder="IWM"
                />
              </div>
              <div className="flex items-end gap-2">
                <ToggleGroup
                  type="single"
                  value={mode}
                  onValueChange={(v) => v && setMode(v as Mode)}
                  variant="outline"
                >
                  <ToggleGroupItem value="setup">Setups</ToggleGroupItem>
                  <ToggleGroupItem value="watch">Watch</ToggleGroupItem>
                  <ToggleGroupItem value="all">All</ToggleGroupItem>
                </ToggleGroup>
                <div className="flex items-center gap-2 pl-4">
                  <Switch
                    id="compact"
                    checked={compact}
                    onCheckedChange={setCompact}
                  />
                  <Label htmlFor="compact" className="text-xs">
                    compact
                  </Label>
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={runAnalyze}
                  disabled={state.kind === "loading" || !settingsLoaded}
                  className="w-full"
                >
                  {state.kind === "loading"
                    ? "Analyzing…"
                    : !settingsLoaded
                      ? "Loading…"
                      : "Analyze"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {state.kind === "error" && (
          <Card className="border-destructive">
            <CardContent className="pt-6 text-destructive">{state.message}</CardContent>
          </Card>
        )}

        {state.kind === "loading" && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {state.kind === "ready" && (
          <RankTable
            rows={rows}
            compact={compact}
            mode={mode}
            snapshots={trackerSnapshots}
            pollIntervalSec={trackPollSeconds}
            onSelect={(t) => setSelected(t)}
          />
        )}

        {state.kind === "ready" && rows.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">
              {mode === "watch"
                ? "No watch candidates: every ticker is already a setup, in an idiosyncratic downtrend, or below the pos threshold."
                : mode === "setup"
                  ? "No tickers currently match a setup. Try All to see the full list or Watch to see stocks to anticipate."
                  : "No tickers analyzed."}
            </CardContent>
          </Card>
        )}

        <ScannerPanel
          settings={currentSettings}
          onSettingsChange={patchSettings}
          onPromote={promoteTicker}
          onSelect={handleScannerSelect}
        />

        {state.kind === "ready" && state.response.statuses.length > 0 && (() => {
          const statuses = state.response.statuses;
          const errorCount = statuses.filter((st) => st.SkippedError).length;
          return (
            <Card>
              <CardContent className="pt-6">
                <details open={errorCount > 0} className="group">
                  <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-muted-foreground select-none">
                    <span className="inline-block transition-transform group-open:rotate-90">
                      ▸
                    </span>
                    <span>
                      Fetch log · {statuses.length} ticker
                      {statuses.length === 1 ? "" : "s"}
                    </span>
                    {errorCount > 0 && (
                      <span className="text-rose-500">
                        · {errorCount} error{errorCount === 1 ? "" : "s"}
                      </span>
                    )}
                  </summary>
                  <ul className="mt-3 text-xs font-mono text-muted-foreground space-y-1">
                    {statuses.map((st, i) => (
                      <li key={i}>
                        {st.SkippedError ? (
                          <span className="text-rose-500">
                            skipped {st.Ticker}: {st.SkippedError}
                          </span>
                        ) : (
                          <>
                            {st.Ticker}: {st.Benchmark} ({st.BenchReason}) ·{" "}
                            {st.CacheHit ? "cached" : "fetched"}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </CardContent>
            </Card>
          );
        })()}
      </main>
      <TickerDetail
        ticker={selected}
        row={selectedRow}
        detail={selectedDetail}
        snapshot={selected ? trackerSnapshots[selected] : undefined}
        pollIntervalSec={trackPollSeconds}
        onClose={() => setSelected(null)}
      />
      <SettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={loadSettingsIntoForm}
      />
      {version && (
        <div
          className="pointer-events-none fixed bottom-2 left-3 select-none font-mono text-[10px] text-muted-foreground/40"
          title="taco build"
        >
          {version}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
